/**
 * METAL PRICES JAVASCRIPT
 * 
 * Unified service for precious metal price management
 * - Fetches live prices from Supabase database
 * - Fetches historical data for charts (10+ years)
 * - Updates hero price displays
 * - Powers interactive charts with multiple timeframes
 * - Handles calculator functionality
 */

// Prevent duplicate initialization
if (!window.metalPriceManager) {

  class MetalPriceManager {
    constructor() {
      // Supabase Configuration
      this.supabaseUrl = 'https://yfnbziyirorwokaagzsb.supabase.co';
      this.supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlmbmJ6aXlpcm9yd29rYWFnenNiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjUzNTE1MzcsImV4cCI6MjA4MDkyNzUzN30.fUpUSlKfwrBWUvcsmLG2ETv05kGelIB_p19UTjvogp0';
      this.supabase = null;

      // Data storage
      this.prices = null;
      this.lastUpdate = null;
      this.GRAMS_PER_OUNCE = 31.1035;

      // Current display settings
      // Current display settings
      this.priceCardCurrency = 'EUR';
      this.chartCurrency = 'EUR';
      this.currentUnit = 'ounce';
      this.exchangeRate = 1.08;

      // Chart management
      this.charts = {};
      this.chartSettings = {};

      this.init();
    }

    async init() {
      try {
        await this.initSupabase();
        await this.fetchSupabasePrices();
        this.updateAllPrices();
        this.initCharts();
        this.initCalculators();
        this.initToggles();

        // Update prices every 5 minutes
        setInterval(async () => {
          try {
            await this.fetchSupabasePrices();
            this.updateAllPrices();
          } catch (e) {
            console.warn('Price update failed:', e);
          }
        }, 300000);

        console.log('MetalPriceManager initialized');
      } catch (error) {
        console.error('Failed to initialize MetalPriceManager:', error);
        this.useFallbackPrices();
        this.updateAllPrices();
      }
    }

    async initSupabase() {
      if (!window.supabase) {
        await this.loadSupabaseClient();
      }
      this.supabase = window.supabase.createClient(this.supabaseUrl, this.supabaseKey);
    }

    async loadSupabaseClient() {
      return new Promise(function (resolve, reject) {
        if (window.supabase) {
          resolve();
          return;
        }

        // Check if script is already loading (from theme.liquid or elsewhere)
        if (document.querySelector('script[src*="supabase-js"]')) {
          var checks = 0;
          var interval = setInterval(function () {
            checks++;
            if (window.supabase) {
              clearInterval(interval);
              resolve();
            }
            // Timeout after 5s
            if (checks > 50) {
              clearInterval(interval);
              // If still missing, fallback to loading it ourselves? 
              // Better to reject or try loading again safely, but let's assume it loads.
              if (window.supabase) resolve(); else reject(new Error('Supabase script found but object not ready'));
            }
          }, 100);
          return;
        }

        var script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    async fetchSupabasePrices() {
      if (!this.supabase) throw new Error('Supabase not initialized');

      var result = await this.supabase
        .from('metal_prices_current')
        .select('*');

      if (result.error) throw result.error;

      this.prices = this.processSupabasePrices(result.data);
      this.lastUpdate = new Date();
      return this.prices;
    }

    processSupabasePrices(supabaseData) {
      var metalMap = { 'XAU': 'gold', 'XAG': 'silver', 'XPT': 'platinum', 'XPD': 'palladium' };
      var prices = {};

      for (var i = 0; i < supabaseData.length; i++) {
        var metalData = supabaseData[i];
        var metalName = metalMap[metalData.metal_symbol];
        if (metalName) {
          prices[metalName] = {
            perGram: parseFloat(metalData.price_per_gram_eur),
            perOunce: parseFloat(metalData.price_eur),
            perGramUsd: parseFloat(metalData.price_per_gram_usd),
            perOunceUsd: parseFloat(metalData.price_usd),
            dayOpen: parseFloat(metalData.day_open_eur),
            dayHigh: parseFloat(metalData.day_high_eur),
            dayLow: parseFloat(metalData.day_low_eur),
            exchangeRate: parseFloat(metalData.exchange_rate),
            currency: 'EUR',
            updatedAt: metalData.updated_at
          };
        }
      }

      if (supabaseData.length > 0) {
        this.exchangeRate = parseFloat(supabaseData[0].exchange_rate) || 1.08;
      }

      prices.timestamp = new Date().toISOString();
      return prices;
    }

    useFallbackPrices() {
      this.prices = {
        gold: { perGram: 118.58, perOunce: 3688.38, currency: 'EUR' },
        silver: { perGram: 1.81, perOunce: 56.33, currency: 'EUR' },
        platinum: { perGram: 53.09, perOunce: 1651.34, currency: 'EUR' },
        palladium: { perGram: 45.89, perOunce: 1427.28, currency: 'EUR' },
        timestamp: new Date().toISOString()
      };
      console.warn('Using fallback prices');
    }

    formatPrice(price, currency) {
      var curr = currency || this.priceCardCurrency;
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: curr,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(price);
    }

    getDisplayPrice(metalData) {
      var price = this.currentUnit === 'gram' ? metalData.perGram : metalData.perOunce;
      if (this.priceCardCurrency === 'USD') {
        price = price * this.exchangeRate;
      }
      return price;
    }

    updateAllPrices() {
      if (!this.prices) return;
      this.updateHeroPrices();
      this.updateHeroNewDesign();
      this.updatePriceTables();
      this.updateGoldSilverRatio();

      // Make prices globally available for other sections (e.g., Metal Ratios)
      window.metalPricesData = this.prices;

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('metalPricesUpdated', {
        detail: this.prices
      }));
    }

    updateSchemaPrice(metalType, price) {
      // Find schema element by ID prefix
      var schemaElement = document.querySelector('[id^="schema-price-"]');

      if (!schemaElement) {
        console.warn('Schema price element not found for metal type:', metalType);
        return;
      }

      try {
        // Parse existing JSON
        var schemaData = JSON.parse(schemaElement.textContent);

        // Update price value (format to 2 decimal places)
        schemaData.currentExchangeRate.price = price.toFixed(2);

        // Update validFrom timestamp
        var now = new Date();
        schemaData.validFrom = now.toISOString();

        // Update the schema element
        schemaElement.textContent = JSON.stringify(schemaData, null, 2);
      } catch (error) {
        console.warn('Failed to update schema price:', error);
      }
    }

    updateHeroPrices() {
      var self = this;
      var elements = document.querySelectorAll('[data-metal-price="live"]');
      elements.forEach(function (element) {
        var metalType = element.getAttribute('data-metal-type');
        var metalData = self.prices[metalType];
        if (metalData) {
          element.innerHTML = self.formatPrice(self.getDisplayPrice(metalData));
        }
      });
    }

    updateHeroNewDesign() {
      var self = this;
      var currency = this.priceCardCurrency; // 'EUR' or 'USD'

      // Update Main Price
      document.querySelectorAll('[data-main-price]').forEach(function (el) {
        var metalType = el.getAttribute('data-metal-type');
        var metalData = self.prices[metalType];

        if (metalData) {
          // Determine price value based on currency
          var price = metalData.perOunce; // Default EUR
          if (currency === 'USD') {
            price = metalData.perOunceUsd || (metalData.perOunce * self.exchangeRate);
          }

          el.textContent = self.formatPrice(price, currency);

          // Update schema price for this metal type
          self.updateSchemaPrice(metalType, price);
        }
      });

      // Update Currency Symbol
      document.querySelectorAll('[data-currency-symbol]').forEach(function (el) {
        el.textContent = currency === 'EUR' ? '€' : '$';
      });

      // Update Timestamp
      var now = new Date();
      var timeStr = now.toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
      document.querySelectorAll('[data-timestamp]').forEach(function (el) {
        el.textContent = 'Stand: ' + timeStr;
      });

      this.updateQuickPriceTable();
      this.updateChangeIndicators();
    }

    updateChangeIndicators() {
      var self = this;
      var currency = this.priceCardCurrency;

      // Target the single badge per card
      var badge = document.querySelector('[data-change-badge]');
      if (!badge) return;

      // Find metal type from parent or context (assuming single metal hero for now)
      var section = badge.closest('.metal-hero-chart');
      var metalType = section ? section.getAttribute('data-metal-type') : 'gold';
      var metalData = this.prices[metalType];

      if (!metalData) return;

      // Calculate Change consistently based on EUR change
      // This guarantees that the % change is identical for both currencies
      var eurOpen = metalData.dayOpen;
      var eurCurrent = metalData.perOunce;

      // Safety check
      if (!eurOpen || eurOpen === 0) eurOpen = eurCurrent;

      // 1. Calculate base percentage from EUR (Source of Truth for movement)
      var rawPercent = 0;
      if (eurOpen > 0) {
        rawPercent = (eurCurrent - eurOpen) / eurOpen;
      }

      // 2. Determine display values based on currency
      var currentPrice, changeAmount;

      if (currency === 'EUR') {
        currentPrice = eurCurrent;
        changeAmount = currentPrice - eurOpen;
      } else {
        // USD
        currentPrice = metalData.perOunceUsd || (metalData.perOunce * self.exchangeRate);

        // Derive absolute change from the consistent percentage
        // Formula: Change = Current - (Current / (1 + %))
        changeAmount = currentPrice - (currentPrice / (1 + rawPercent));
      }

      var changePercent = rawPercent * 100;
      var isPositive = changeAmount >= 0;

      // Update Badge Classes
      badge.classList.remove('metal-hero-chart__price-change--positive', 'metal-hero-chart__price-change--negative');
      badge.classList.add(isPositive ? 'metal-hero-chart__price-change--positive' : 'metal-hero-chart__price-change--negative');

      // Update Content
      var iconEl = badge.querySelector('[data-change-icon]');
      var amountEl = badge.querySelector('[data-change-amount]');
      var percentEl = badge.querySelector('[data-change-percent]');

      if (iconEl) iconEl.textContent = isPositive ? '\u25B2' : '\u25BC';
      if (amountEl) amountEl.textContent = (isPositive ? '+' : '') + self.formatPrice(changeAmount, currency);
      if (percentEl) percentEl.textContent = (isPositive ? '+' : '') + changePercent.toFixed(2) + '%';
    }

    updateQuickPriceTable() {
      var section = document.querySelector('.metal-hero-chart');
      if (!section) return;

      var metalType = section.getAttribute('data-metal-type');
      var metalData = this.prices[metalType];
      if (!metalData) return;

      var self = this;
      var currency = this.priceCardCurrency;
      var isUsd = currency === 'USD';

      // Base prices
      var perGram = isUsd ? (metalData.perGramUsd || metalData.perGram * this.exchangeRate) : metalData.perGram;
      var perOunce = isUsd ? (metalData.perOunceUsd || metalData.perOunce * this.exchangeRate) : metalData.perOunce;

      var stats = {
        '1g': perGram,
        '1oz': perOunce,
        '1kg': perGram * 1000
      };

      Object.keys(stats).forEach(function (key) {
        var cell = document.querySelector('[data-quick-stat="' + key + '"]');
        if (cell) {
          cell.textContent = self.formatPrice(stats[key], currency);
        }
      });
    }

    updateGoldSilverRatio() {
      if (!this.prices.gold || !this.prices.silver) return;
      var ratio = this.prices.gold.perOunce / this.prices.silver.perOunce;
      document.querySelectorAll('[data-gold-silver-ratio]').forEach(function (el) {
        el.textContent = ratio.toFixed(2);
      });
    }

    updatePriceTables() {
      var self = this;
      var currencyMultiplier = this.currentCurrency === 'USD' ? this.exchangeRate : 1;

      document.querySelectorAll('[data-price-cell]').forEach(function (cell) {
        var cellId = cell.getAttribute('data-price-cell');
        var metalType = cell.getAttribute('data-metal');
        var metalData = self.prices[metalType];
        if (!metalData) return;

        if (cellId.indexOf('weight-') === 0) {
          var weightStr = cellId.replace('weight-', '');
          var isGram = weightStr.indexOf('gram') > -1;
          var isOunce = weightStr.indexOf('ounce') > -1;
          var weight = parseFloat(weightStr.replace(/[^0-9.]/g, ''));

          var price;
          if (isGram) price = metalData.perGram * weight;
          else if (isOunce) price = metalData.perOunce * weight;
          else price = metalData.perGram * weight;

          if (price && !isNaN(price)) {
            cell.innerHTML = self.formatPrice(price * currencyMultiplier);
          }
        }

        if (cellId.indexOf('alloy-') === 0) {
          var purity = parseFloat(cellId.replace('alloy-', ''));
          var pricePerGram = metalData.perGram * (purity / 1000);
          cell.innerHTML = self.formatPrice(pricePerGram * currencyMultiplier);
        }
      });
    }

    initCalculators() {
      var self = this;
      document.querySelectorAll('[data-metal-type]').forEach(function (calculator) {
        if (!calculator.querySelector('[data-calculator-button]')) return;

        var button = calculator.querySelector('[data-calculator-button]');
        var weightInput = calculator.querySelector('[data-calculator-input="weight"]');
        var puritySelect = calculator.querySelector('[data-calculator-input="purity"]');
        var resultElement = calculator.querySelector('[data-calculator-output]');

        if (!button || !weightInput || !puritySelect || !resultElement) return;

        button.addEventListener('click', function () {
          self.calculateValue(calculator, weightInput, puritySelect, resultElement);
        });

        weightInput.addEventListener('keypress', function (e) {
          if (e.key === 'Enter') {
            self.calculateValue(calculator, weightInput, puritySelect, resultElement);
          }
        });
      });
    }

    calculateValue(calculator, weightInput, puritySelect, resultElement) {
      var metalType = calculator.getAttribute('data-metal-type');
      var weight = parseFloat(weightInput.value);
      var purity = parseFloat(puritySelect.value);

      if (!weight || !purity || !this.prices || !this.prices[metalType]) {
        resultElement.textContent = 'Bitte alle Felder ausfüllen';
        return;
      }

      var metalData = this.prices[metalType];
      var value = metalData.perGram * weight * (purity / 1000);
      resultElement.textContent = this.formatPrice(value);
    }

    initCharts() {
      var self = this;
      this.charts = {};
      this.chartSettings = {};

      document.querySelectorAll('.metal-hero-chart__tabs').forEach(function (tabContainer) {
        var tabs = tabContainer.querySelectorAll('.metal-hero-chart__tab');
        var canvasId = tabContainer.getAttribute('data-chart-canvas');
        var metalType = tabContainer.getAttribute('data-metal-type');

        var activeTab = tabContainer.querySelector('.metal-hero-chart__tab--active');
        var initialDays = activeTab ? parseInt(activeTab.getAttribute('data-days')) : 30;

        self.chartSettings[canvasId] = { metalType: metalType, days: initialDays };

        if (canvasId && metalType) {
          self.loadChartData(canvasId, metalType, initialDays);
        }

        tabs.forEach(function (tab) {
          tab.addEventListener('click', function () {
            var days = parseInt(tab.getAttribute('data-days'));

            tabs.forEach(function (t) {
              t.classList.remove('metal-hero-chart__tab--active');
              t.setAttribute('aria-pressed', 'false');
            });
            tab.classList.add('metal-hero-chart__tab--active');
            tab.setAttribute('aria-pressed', 'true');

            if (self.chartSettings[canvasId]) {
              self.chartSettings[canvasId].days = days;
            }

            if (canvasId && metalType) {
              self.loadChartData(canvasId, metalType, days);
            }
          });
        });
      });

      this.initChartControls();
    }

    initChartControls() {
      var self = this;
      // Removed old global currency selector logic as we use dedicated toggles now

      document.querySelectorAll('[data-chart-unit]').forEach(function (select) {
        select.addEventListener('change', function (e) {
          self.currentUnit = e.target.value;
          self.updateAllPrices();
          self.reloadAllCharts();
        });
      });
    }

    reloadAllCharts() {
      var self = this;
      Object.keys(this.chartSettings).forEach(function (canvasId) {
        var settings = self.chartSettings[canvasId];
        self.loadChartData(canvasId, settings.metalType, settings.days);
      });
    }

    async loadChartData(canvasId, metalType, days) {
      var canvas = document.getElementById(canvasId);
      var loadingEl = document.getElementById(canvasId.replace('metalChart', 'ChartLoading'));

      if (!canvas) return;
      if (loadingEl) loadingEl.classList.remove('hidden');

      try {
        var chartData = await this.fetchChartDataEfficient(metalType, days);
        this.renderChart(canvasId, chartData, metalType);
      } catch (error) {
        console.error('Chart error:', error);
        var demoData = this.generateDemoChartData(metalType, days);
        this.renderChart(canvasId, demoData, metalType);
      }

      if (loadingEl) loadingEl.classList.add('hidden');
    }

    async fetchChartDataEfficient(metalType, days) {
      var tableMap = {
        'gold': 'gold_prices_history',
        'silver': 'silver_prices_history',
        'platinum': 'platinum_prices_history',
        'palladium': 'palladium_prices_history'
      };

      var tableName = tableMap[metalType];
      if (!tableName || !this.supabase) {
        return this.generateDemoChartData(metalType, days);
      }

      // 1. Berechne gewuenschte Anzahl Datenpunkte
      var targetPoints;
      if (days <= 14) targetPoints = days + 1;
      else if (days <= 30) targetPoints = 30;
      else if (days <= 90) targetPoints = 45;
      else if (days <= 365) targetPoints = 52;
      else if (days <= 1825) targetPoints = 60;
      else targetPoints = 50;

      // 2. Berechne die gewuenschten Datumspunkte (von heute rueckwaerts)
      var today = new Date();
      var targetDates = [];

      for (var i = 0; i < targetPoints; i++) {
        var daysBack = Math.round((i * days) / (targetPoints - 1));
        var date = new Date(today);
        date.setDate(today.getDate() - daysBack);
        targetDates.push(date.toISOString().split('T')[0]);
      }

      // Umkehren: aeltestes Datum zuerst
      targetDates.reverse();

      console.log('Lade ' + metalType + ': ' + targetPoints + ' gezielte Datenpunkte');
      console.log('Zeitraum: ' + targetDates[0] + ' bis ' + targetDates[targetDates.length - 1]);

      // 3. EFFIZIENTE ABFRAGE: Nur die gewuenschten Daten holen
      var result = await this.supabase
        .from(tableName)
        .select('price_date, close_eur, price_usd')
        .in('price_date', targetDates)
        .order('price_date', { ascending: true });

      if (result.error) throw result.error;

      var rawData = result.data || [];
      console.log('Abgefragt: ' + targetDates.length + ' Daten, Erhalten: ' + rawData.length);

      if (rawData.length === 0) {
        return this.generateDemoChartData(metalType, days);
      }

      // 4. Wenn keine Daten gefunden, hole alle Daten im Bereich und sample sie
      if (rawData.length < targetPoints / 2) {
        console.log('Zu wenige exakte Treffer, lade Bereichsdaten...');

        var startDate = targetDates[0];
        var endDate = targetDates[targetDates.length - 1];

        var rangeResult = await this.supabase
          .from(tableName)
          .select('price_date, close_eur, price_usd')
          .gte('price_date', startDate)
          .lte('price_date', endDate)
          .order('price_date', { ascending: true })
          .limit(1000);

        if (!rangeResult.error && rangeResult.data && rangeResult.data.length > 0) {
          rawData = rangeResult.data;
          console.log('Bereichsabfrage: ' + rawData.length + ' Datenpunkte gefunden');
        }
      }

      // 5. Sample die Daten gleichmaessig
      var chartData = [];

      if (rawData.length <= targetPoints) {
        chartData = rawData;
      } else {
        var step = (rawData.length - 1) / (targetPoints - 1);
        for (var k = 0; k < targetPoints; k++) {
          var idx = Math.round(k * step);
          chartData.push(rawData[idx]);
        }
      }

      console.log('Gesampelte Daten: ' + chartData.length + ' Punkte');

      // 5. Heutigen Live-Preis hinzufuegen/ersetzen
      var todayStr = today.toISOString().split('T')[0];
      var metalData = this.prices[metalType];
      var self = this;

      if (metalData) {
        chartData = chartData.filter(function (d) { return d.price_date !== todayStr; });

        chartData.push({
          price_date: todayStr,
          close_eur: metalData.perOunce.toString(),
          // IMPORTANT: Use the REAL USD price from DB if available to avoid "drops" caused by exchange rate diffs
          price_usd: (metalData.perOunceUsd || (metalData.perOunce * self.exchangeRate)).toString(),
          isLive: true
        });
      }

      // Sortiere nach Datum
      chartData.sort(function (a, b) { return a.price_date.localeCompare(b.price_date); });

      console.log('Chart-Daten: ' + chartData.length + ' Punkte');

      // 6. Konvertiere zu Chart-Format
      var labels = [];
      var prices = [];
      var fullDates = [];

      chartData.forEach(function (dataPoint) {
        var date = new Date(dataPoint.price_date);

        // Kurzes Label fuer X-Achse
        var label;
        if (days <= 30) {
          label = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
        } else if (days <= 365) {
          label = date.toLocaleDateString('de-DE', { month: 'short', day: '2-digit' });
        } else {
          label = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
        }
        labels.push(label);

        // Volles Datum fuer Tooltip
        var fullDate = date.toLocaleDateString('de-DE', {
          weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'
        });
        if (dataPoint.isLive) fullDate += ' (LIVE)';
        fullDates.push(fullDate);

        // Preis basierend auf Waehrung
        var price = self.chartCurrency === 'USD' ?
          parseFloat(dataPoint.price_usd || dataPoint.close_eur * self.exchangeRate) :
          parseFloat(dataPoint.close_eur);
        prices.push(price);
      });

      console.log('Chart-Daten: ' + labels.length + ' Punkte, von ' + labels[0] + ' bis ' + labels[labels.length - 1]);

      return { labels: labels, prices: prices, fullDates: fullDates };
    }

    generateDemoChartData(metalType, days) {
      var basePrices = { 'gold': 2650, 'silver': 30, 'platinum': 950, 'palladium': 1000 };
      var basePrice = basePrices[metalType] || 2650;
      var labels = [];
      var prices = [];
      var fullDates = [];

      var dataPoints = Math.min(days, 50);
      var currentPrice = basePrice;
      var now = new Date();

      for (var i = 0; i < dataPoints; i++) {
        var pointDate = new Date(now);
        pointDate.setDate(pointDate.getDate() - (dataPoints - i));

        if (days <= 30) {
          labels.push(pointDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }));
        } else {
          labels.push(pointDate.toLocaleDateString('de-DE', { month: 'short', year: '2-digit' }));
        }

        fullDates.push(pointDate.toLocaleDateString('de-DE', {
          weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'
        }));

        var change = (Math.random() - 0.5) * 0.02;
        currentPrice = currentPrice * (1 + change);
        prices.push(currentPrice);
      }

      return { labels: labels, prices: prices, fullDates: fullDates };
    }

    renderChart(canvasId, data, metalType) {
      var canvas = document.getElementById(canvasId);
      if (!canvas) return;

      // Destroy existing chart
      if (this.charts[canvasId]) {
        this.charts[canvasId].destroy();
      }

      var labels = data.labels;
      var prices = data.prices.slice();
      var fullDates = data.fullDates || labels;

      // Convert to gram if needed
      if (this.currentUnit === 'gram') {
        var gramsPerOunce = this.GRAMS_PER_OUNCE;
        prices = prices.map(function (p) { return p / gramsPerOunce; });
      }

      // Colors - einheitliches Shop-Blau fuer alle Metalle
      var shopBlue = '#002D40';
      var colors = {
        line: shopBlue,
        gradient: ['rgba(0, 45, 64, 0.3)', 'rgba(0, 45, 64, 0.0)']
      };
      var unitLabel = this.currentUnit === 'gram' ? 'g' : 'oz';
      var currencySymbol = this.chartCurrency === 'EUR' ? 'EUR ' : 'USD ';

      // Create gradient
      var ctx = canvas.getContext('2d');
      var gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, colors.gradient[0]);
      gradient.addColorStop(1, colors.gradient[1]);

      // Store fullDates for tooltip access (closure)
      var chartFullDates = fullDates;

      // Create chart
      this.charts[canvasId] = new Chart(canvas, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: metalType.charAt(0).toUpperCase() + metalType.slice(1),
            data: prices,
            borderColor: colors.line,
            backgroundColor: gradient,
            borderWidth: 2,
            fill: true,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: colors.line,
            pointHoverBorderColor: '#ffffff',
            pointHoverBorderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              backgroundColor: 'rgba(26, 26, 26, 0.95)',
              titleColor: '#ffffff',
              bodyColor: '#ffffff',
              borderColor: colors.line,
              borderWidth: 2,
              padding: 12,
              displayColors: false,
              callbacks: {
                title: function (tooltipItems) {
                  var index = tooltipItems[0].dataIndex;
                  return chartFullDates[index] || tooltipItems[0].label;
                },
                label: function (tooltipItem) {
                  var value = tooltipItem.parsed.y.toFixed(2);
                  var perGram = (tooltipItem.parsed.y / 31.1035).toFixed(2);
                  if (unitLabel === 'g') {
                    return [currencySymbol + value + ' / g'];
                  }
                  return [currencySymbol + value + ' / oz', currencySymbol + perGram + ' / g'];
                }
              }
            }
          },
          scales: {
            x: {
              display: true,
              grid: { display: false },
              ticks: { maxTicksLimit: 8, color: '#9ca3af', font: { size: 11 } },
              border: { display: false }
            },
            y: {
              display: true,
              position: 'right',
              grid: { color: 'rgba(0,0,0,0.05)', drawBorder: false },
              ticks: {
                color: '#9ca3af',
                font: { size: 11 },
                callback: function (value) { return currencySymbol + value.toFixed(0); }
              },
              border: { display: false }
            }
          }
        }
      });
    }

    initToggles() {
      var self = this;

      document.querySelectorAll('[data-toggle-type="currency"]').forEach(function (container) {
        container.querySelectorAll('.metal-hero-chart__toggle').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var context = container.getAttribute('data-toggle-context'); // 'price-card' or 'chart'

            container.querySelectorAll('.metal-hero-chart__toggle').forEach(function (b) {
              b.classList.remove('metal-hero-chart__toggle--active');
            });
            btn.classList.add('metal-hero-chart__toggle--active');

            var selectedCurrency = btn.getAttribute('data-value');

            if (context === 'price-card') {
              self.priceCardCurrency = selectedCurrency;
              self.updateAllPrices(); // Only updates price texts
            } else if (context === 'chart') {
              self.chartCurrency = selectedCurrency;
              self.reloadAllCharts(); // Only reload charts
            } else {
              // Fallback for backward compatibility
              self.priceCardCurrency = selectedCurrency;
              self.chartCurrency = selectedCurrency;
              self.updateAllPrices();
              self.reloadAllCharts();
            }
          });
        });
      });

      document.querySelectorAll('[data-toggle-type="unit"]').forEach(function (container) {
        container.querySelectorAll('.metal-hero-chart__toggle').forEach(function (btn) {
          btn.addEventListener('click', function () {
            container.querySelectorAll('.metal-hero-chart__toggle').forEach(function (b) {
              b.classList.remove('metal-hero-chart__toggle--active');
            });
            btn.classList.add('metal-hero-chart__toggle--active');
            self.currentUnit = btn.getAttribute('data-value');
            self.updateAllPrices();
            self.reloadAllCharts();
          });
        });
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window.metalPriceManager = new MetalPriceManager();
    });
  } else {
    window.metalPriceManager = new MetalPriceManager();
  }

} // End of duplicate prevention guard
